<template>
  <div
    class="avatar inline-flex items-center justify-center rounded-full overflow-hidden"
    :class="[sizeClass, variantClass, { 'ring-2 ring-white': ring }]"
    :style="avatarStyle"
  >
    <img
      v-if="src && !error"
      :src="src"
      :alt="alt"
      @error="onError"
      class="w-full h-full object-cover"
    />
    <div
      v-else
      class="avatar-fallback w-full h-full flex items-center justify-center"
      :class="fallbackClass"
    >
      <span class="avatar-initials font-semibold">{{ initials }}</span>
      <slot name="fallback">
        <i v-if="icon" :class="icon" class="avatar-icon"></i>
      </slot>
    </div>
    <div v-if="status" class="avatar-status" :class="statusClass"></div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";

const props = defineProps({
  src: {
    type: String,
    default: "",
  },
  alt: {
    type: String,
    default: "Avatar",
  },
  name: {
    type: String,
    default: "",
  },
  size: {
    type: String,
    default: "md",
    validator: (value) => ["xs", "sm", "md", "lg", "xl", "2xl"].includes(value),
  },
  variant: {
    type: String,
    default: "primary",
    validator: (value) =>
      ["primary", "secondary", "success", "warning", "error", "info"].includes(
        value
      ),
  },
  status: {
    type: String,
    default: "",
    validator: (value) =>
      ["online", "offline", "away", "busy", ""].includes(value),
  },
  ring: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: String,
    default: "",
  },
  backgroundColor: {
    type: String,
    default: "",
  },
  textColor: {
    type: String,
    default: "",
  },
});

const error = ref(false);

const onError = () => {
  error.value = true;
};

const initials = computed(() => {
  if (!props.name) return "";
  const parts = props.name.split(" ");
  return parts
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase()
    .substring(0, 2);
});

const sizeClass = computed(() => {
  const sizes = {
    xs: "w-6 h-6 text-xs",
    sm: "w-8 h-8 text-sm",
    md: "w-10 h-10 text-base",
    lg: "w-12 h-12 text-lg",
    xl: "w-16 h-16 text-xl",
    "2xl": "w-20 h-20 text-2xl",
  };
  return sizes[props.size];
});

const variantClass = computed(() => {
  const variants = {
    primary: "bg-primary-100 text-primary-800",
    secondary: "bg-gray-100 text-gray-800",
    success: "bg-green-100 text-green-800",
    warning: "bg-yellow-100 text-yellow-800",
    error: "bg-red-100 text-red-800",
    info: "bg-cyan-100 text-cyan-800",
  };
  return variants[props.variant];
});

const fallbackClass = computed(() => {
  return props.backgroundColor ? "" : variantClass.value;
});

const avatarStyle = computed(() => {
  const style = {};
  if (props.backgroundColor) {
    style.backgroundColor = props.backgroundColor;
  }
  if (props.textColor) {
    style.color = props.textColor;
  }
  return style;
});

const statusClass = computed(() => {
  const base =
    "avatar-status absolute bottom-0 right-0 rounded-full border-2 border-white";
  const statusMap = {
    online: "bg-green-500",
    offline: "bg-gray-400",
    away: "bg-yellow-500",
    busy: "bg-red-500",
  };
  const sizeMap = {
    xs: "w-1.5 h-1.5",
    sm: "w-2 h-2",
    md: "w-2.5 h-2.5",
    lg: "w-3 h-3",
    xl: "w-4 h-4",
    "2xl": "w-5 h-5",
  };
  return `${base} ${statusMap[props.status] || ""} ${sizeMap[props.size]}`;
});
</script>

<style scoped>
.avatar {
  position: relative;
  flex-shrink: 0;
}

.avatar-status {
  position: absolute;
}
</style>
